import type { RiskModelType } from "@unbox/api";
import type { RiskCategory } from "@unbox/db";
import {
  CheckVerified02,
  Globe04,
  MusicNote01,
  Package,
  Shield01,
  ShieldPlus,
  Tag01,
  Users01,
} from "@untitledui/icons";
import type { ComponentType } from "react";

// The subset of a risk catalog row this module needs. Both the merchant and the
// workspace catalog responses (Risk & { tier }) satisfy it, so the grouping is
// scope-agnostic.
export type RiskCatalogEntry = {
  id: string;
  readableName: string;
  readableDescription: string;
  category: string;
  modelType: RiskModelType;
  tier: number;
  config: unknown;
};

// Frontend-only display severity, derived from a risk's DB tier (see
// tierToSeverity). There is no severity column in the DB.
export type Severity = "low" | "medium" | "high" | "critical";

export type RiskItem = {
  key: string;
  // All underlying risk ids collapsed under this row (risks that share a
  // readable name are merged into one). Defaults to `[key]` for static items.
  keys?: string[];
  name: string;
  desc: string;
  // DB risk category (e.g. "custom", "baseline"...) — see canEditCreatorRiskExplanation
  // for which categories/risks can set the creator risk explanation field.
  category: string;
  defaultSeverity: Severity;
  modelType: RiskModelType;
  // Catalog (default) detection config — read for keyword field defaults and to
  // detect `{placeholder}` tokens in string fields (e.g. debate.description).
  config: Record<string, unknown>;
};

export type RiskGroup = {
  id: string;
  name: string;
  items: RiskItem[];
};

// A risk's `key` is its own catalog id, but merged rows also carry every underlying id
// in `keys` — either can show up in a URL selection, so both are checked.
export const matchesRisk = (item: RiskItem, riskId: string): boolean =>
  item.key === riskId || !!item.keys?.includes(riskId);

// Resolves a URL-selected id (either a specific risk's id, or a group's own id) to the
// group it belongs to — undefined when the id matches neither, i.e. a stale/unknown link.
export const findGroupIdForSelection = (groups: RiskGroup[], selectedId: string): string | undefined =>
  groups.find((g) => g.id === selectedId || g.items.some((item) => matchesRisk(item, selectedId)))?.id;

// Display name + ordering for each risk category (matches the DB `risk_category_enum`) — the
// single source of truth for category labels across the app (Risk Catalog, Configure Safety,
// assurance tools). Drives the grouping of the catalog-driven risks table.
export const CATEGORY_META: { id: RiskCategory; name: string }[] = [
  { id: "baseline", name: "Core Safety" },
  { id: "claims_and_messaging", name: "Claims & messaging" },
  { id: "third_parties", name: "Third parties" },
  { id: "participants", name: "Audience & participants" },
  { id: "product_depiction", name: "Product depiction" },
  { id: "music", name: "Music" },
  { id: "authenticity_and_disclosure", name: "Authenticity & disclosure" },
  { id: "custom", name: "Custom" },
];

// Lookup form of CATEGORY_META, for callers indexing by category instead of iterating.
export const CATEGORY_LABELS: Record<RiskCategory, string> = Object.fromEntries(
  CATEGORY_META.map(({ id, name }) => [id, name]),
) as Record<RiskCategory, string>;

// Icon shown next to each group's name — shared by the read-only Risks tab
// (RiskGroupAccordion/RiskSummaryBand) and the Configure Safety modal.
export const GROUP_ICONS: Record<string, ComponentType<{ className?: string; "aria-hidden"?: "true" }>> = {
  baseline: Shield01,
  claims_and_messaging: Tag01,
  third_parties: Globe04,
  participants: Users01,
  product_depiction: Package,
  music: MusicNote01,
  authenticity_and_disclosure: CheckVerified02,
  custom: ShieldPlus,
};

// Severity is derived from the risk's (DB-coalesced) tier: lower tier = more
// severe. Tiers outside 1–4 clamp to the nearest end.
export const tierToSeverity = (tier: number): Severity => {
  if (tier <= 1) return "critical";
  if (tier === 2) return "high";
  if (tier === 3) return "medium";
  return "low";
};

// Shared tooltip copy for the "Risk explanation for creators" field, shown next
// to both the read-only display (RiskRow) and the edit textarea (ModalRiskRow).
export const CREATOR_RISK_EXPLANATION_TOOLTIP =
  `The text here fills the {creator_risk_explanation} variable in creator messages. Keep it ` +
  `specific to this product — say exactly what isn't allowed and why, rather than generic wording ` +
  `like "not suitable for all audiences." Example: "References to constipation, regularity, or ` +
  `bathroom habits aren't permitted. Claims that this product aids constipation are explicitly not supported."`;

// Core Safety ("baseline" category) risks can never be disabled — see UNB-2098.
export const CORE_SAFETY_LOCKED_TOOLTIP = "Core Safety Risks cannot be disabled";

// Risks (by DB `risks.id`) allowed to set the creator-facing explanation override outside
// the "custom" category. The backend already resolves this per-riskId for every risk
// regardless of category — this list is purely the frontend's decision about which
// non-custom risks expose the editor today (UNB-2416: Unlicensed Music).
export const EDITABLE_CREATOR_EXPLANATION_RISK_IDS = new Set(["unlicensed_popular_music_detection"]);

export const canEditCreatorRiskExplanation = (item: { category: string; key: string }): boolean =>
  item.category === "custom" || EDITABLE_CREATOR_EXPLANATION_RISK_IDS.has(item.key);

// Shown as the edit textarea's placeholder so merchants can see what's currently sent before
// overriding it. This is the fixed (non-LLM) creator explanation insights-pipeline sends
// whenever a risk is a video's only confirmed risk — verbatim from
// UNLICENSED_MUSIC_CREATOR_EXPLANATION in insights-pipeline's risk_detection handler.
export const RISK_EXPLANATION_DEFAULT_PLACEHOLDERS: Record<string, string> = {
  unlicensed_popular_music_detection:
    "We are unable to verify the commercial usage rights for the music you've used. Please ensure that you use music from TikTok's Commercial Music Library.",
};

export const severityToTier = (severity: Severity): number => {
  if (severity === "critical") return 1;
  if (severity === "high") return 2;
  if (severity === "medium") return 3;
  return 4;
};

// Canonical iteration order, worst to least severe.
export const SEVERITY_ORDER: Severity[] = ["critical", "high", "medium", "low"];

// Plain display text for a severity — the single source of truth, replacing the
// half-dozen local "Critical"/"High"/"Medium"/"Low" copies scattered across safety-config,
// admin, assurance, and filter panels.
export const SEVERITY_LABEL: Record<Severity, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

// Convenience wrapper for the common "I have a raw tier number, I want the display word"
// case (e.g. `Table.Cell`s, `<Select>` options) — skips the intermediate Severity key.
export const tierToSeverityLabel = (tier: number): string => SEVERITY_LABEL[tierToSeverity(tier)];

// Color for a severity — a single raw hex, one shape for every consumer (applied via inline
// `style`, not a Tailwind class). Only meant for severity pickers (a <Select>/<Dropdown> where
// the user picks one of the four options) — read-only severity badges elsewhere keep their own
// color on purpose (some intentionally match a specific Figma), and chart legends (e.g. the
// home/safety-overview breakdown cards) are a separate concern, not consolidated here.
export const SEVERITY_COLOR: Record<Severity, string> = {
  critical: "#d42b2b",
  high: "#f79009",
  medium: "#eaaa08",
  low: "#9ca3af",
};

// Label + color together, for severity pickers that render both per option in one place.
export const SEVERITY_META: Record<Severity, { label: string; color: string }> = Object.fromEntries(
  SEVERITY_ORDER.map((severity) => [severity, { label: SEVERITY_LABEL[severity], color: SEVERITY_COLOR[severity] }]),
) as Record<Severity, { label: string; color: string }>;

// Shared grouping primitive: buckets rows by category (in CATEGORY_META order,
// dropping categories with no rows), collapsing rows that share a readable name
// into a single item via `merge`. Used by both `buildRiskGroupsFromCatalog` (the
// Configure Safety / "add risk" catalog) and `buildMerchantRiskFilterGroups` (the
// Creators/Videos risk-type filter, UNB-2388) so the two stay structurally in
// sync instead of maintaining the same category-order/readableName-merge rules
// twice.
export function buildGroupsByCategory<TRow, TItem extends { name: string }>(
  rows: TRow[],
  getReadableName: (row: TRow) => string,
  getCategory: (row: TRow) => string,
  create: (row: TRow) => TItem,
  merge: (existing: TItem, row: TRow) => void,
): { id: string; name: string; items: TItem[] }[] {
  const byCategory = new Map<string, TItem[]>();

  for (const row of rows) {
    const category = getCategory(row);
    const items = byCategory.get(category) ?? [];
    byCategory.set(category, items);

    const existing = items.find((item) => item.name === getReadableName(row));
    if (existing) {
      merge(existing, row);
      continue;
    }
    items.push(create(row));
  }

  return CATEGORY_META.map(({ id, name }) => ({ id, name, items: byCategory.get(id) ?? [] })).filter(
    (g) => g.items.length > 0,
  );
}

// Builds the grouped risk list from the merchant's DB catalog (all global risks
// plus the merchant's enabled custom risks), grouped by category.
export const buildRiskGroupsFromCatalog = (catalog: RiskCatalogEntry[]): RiskGroup[] =>
  buildGroupsByCategory<RiskCatalogEntry, RiskItem>(
    catalog,
    (risk) => risk.readableName,
    (risk) => risk.category,
    (risk) => ({
      key: risk.id,
      keys: [risk.id],
      name: risk.readableName,
      desc: risk.readableDescription,
      category: risk.category,
      defaultSeverity: tierToSeverity(risk.tier),
      modelType: risk.modelType,
      config: (risk.config as Record<string, unknown>) ?? {},
    }),
    // Collapse risks that share a readable name into a single row, tracking all
    // their underlying ids so enable/disable applies to every merged risk.
    (existing, risk) => existing.keys?.push(risk.id),
  );
